import {
  EndpointBuilder,
  QueryDefinition,
  MutationDefinition,
  BaseQueryFn,
  InfiniteQueryDefinition,
} from '@reduxjs/toolkit/query';

import type {
  IProjectShift,
  IProjectShiftWorkplannerApiResponse,
} from 'interfaces/ProjectShift.interface';
import type {
  ICollection,
  ICollectionApiResponse,
  IInfinitePaginationArgs,
} from 'services/api/api.types';
import type {
  IProjectShiftConfirmBriefingArgs,
  IProjectShiftCollectionArgs,
  IProjectShiftCollectionResponse,
  IProjectShiftArgs,
  IProjectShiftPutStatusArgs,
  IProjectShiftPutArgs,
  IProjectShiftRemindPutArgs,
  IProjectShiftWorkplannerCollectionResponse,
  IProjectShiftLivemodePutArgs,
} from './projectShifts.api.types';
import {
  serializeFilters,
  serializeFlatObject,
} from 'services/helper/getPaginationParams.helper';
import { transformShiftWorkplannerCollectionResponse } from './transformShiftCollectionResponse.helper';
import { transformCollectionResponse } from 'services/teamHeroApi.helpers';

export const getProjectShifts = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): QueryDefinition<
  IProjectShiftCollectionArgs,
  BaseQueryFn,
  string,
  IProjectShiftCollectionResponse
> =>
  build.query<IProjectShiftCollectionResponse, IProjectShiftCollectionArgs>({
    providesTags: ['ProjectShift'],
    query: (queryArg: IProjectShiftCollectionArgs) => {
      const paginationParamObject = {
        page: queryArg.page,
        itemsPerPage: queryArg.itemsPerPage,
        pagination: queryArg.pagination,
        contact: queryArg.contact,
        during: queryArg.during,
        ...queryArg.order,
      };
      const paginationQuery = serializeFlatObject(paginationParamObject, '?');
      const filterQuery = serializeFilters(queryArg.filters);

      return {
        url: `api/project_shifts${paginationQuery}${filterQuery}`,
      };
    },
    transformResponse: (
      response: ICollectionApiResponse<IProjectShift>
    ): IProjectShiftCollectionResponse =>
      transformCollectionResponse<IProjectShift>(response),
  });

export const getInfiniteProjectShifts = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): InfiniteQueryDefinition<
  IProjectShiftCollectionArgs,
  IInfinitePaginationArgs,
  BaseQueryFn,
  string,
  ICollection<IProjectShift>
> =>
  build.infiniteQuery<
    ICollection<IProjectShift>,
    IProjectShiftCollectionArgs,
    IInfinitePaginationArgs
  >({
    providesTags: ['ProjectShift'],
    infiniteQueryOptions: {
      // Must provide a default initial page param value
      initialPageParam: {
        page: 1,
        itemsPerPage: 10,
      },
      getNextPageParam: (
        lastPage,
        _allPages,
        { itemsPerPage, page: pageLastPage },
        _allPageParams
      ) => {
        const totalCount = lastPage?.totalCount;
        if (!totalCount || !itemsPerPage) {
          return undefined;
        }
        if (totalCount <= itemsPerPage * pageLastPage || totalCount === 0) {
          return undefined;
        }
        return { page: pageLastPage + 1, itemsPerPage };
      },
      getPreviousPageParam: (
        _firstPage,
        _allPages,
        { page: pageFirstPage, itemsPerPage },
        _allPageParams
      ) => {
        const prevPage = pageFirstPage - 1;
        if (prevPage < 1) {
          return undefined;
        }
        return { page: prevPage, itemsPerPage };
      },
    },
    query: ({ queryArg, pageParam: { page, itemsPerPage } }) => {
      const paginationParamObject = {
        page,
        itemsPerPage,
        pagination: queryArg.pagination,
        ...queryArg.order,
      };
      const paginationQuery = serializeFlatObject(paginationParamObject, '?');
      const filterQuery = serializeFilters(queryArg.filters);
      return `api/project_shifts${paginationQuery}${filterQuery}`;
    },
    transformResponse: (
      response: ICollectionApiResponse<IProjectShift>
    ): ICollection<IProjectShift> =>
      transformCollectionResponse<IProjectShift>(response),
  });

export const getProjectShiftsWorkplanner = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): QueryDefinition<
  IProjectShiftCollectionArgs,
  BaseQueryFn,
  string,
  IProjectShiftWorkplannerCollectionResponse
> =>
  build.query<
    IProjectShiftWorkplannerCollectionResponse,
    IProjectShiftCollectionArgs
  >({
    providesTags: ['ProjectShift'],
    query: (queryArg: IProjectShiftCollectionArgs) => {
      const paginationParamObject = {
        page: queryArg.page,
        itemsPerPage: queryArg.itemsPerPage,
        pagination: queryArg.pagination,
        ...queryArg.order,
      };
      const paginationQuery = serializeFlatObject(paginationParamObject, '?');
      const filterQuery = serializeFilters(queryArg.filters);

      return {
        url: `api/project_shifts/workplanner${paginationQuery}${filterQuery}`,
      };
    },
    transformResponse: (
      response: ICollectionApiResponse<IProjectShiftWorkplannerApiResponse>
    ): IProjectShiftWorkplannerCollectionResponse =>
      transformShiftWorkplannerCollectionResponse(response),
  });

export const getProjectShiftItem = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): QueryDefinition<IProjectShiftArgs, BaseQueryFn, string, IProjectShift> =>
  build.query<IProjectShift, IProjectShiftArgs>({
    providesTags: ['ProjectShift'],
    query: (queryArg: IProjectShiftArgs) => ({
      url: `/api/project_shifts/${queryArg.id}`,
    }),
  });

export const putProjectShiftItem = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): MutationDefinition<
  IProjectShiftPutArgs,
  BaseQueryFn,
  string,
  IProjectShift
> =>
  build.mutation<IProjectShift, IProjectShiftPutArgs>({
    invalidatesTags: ['ProjectShift'],
    query: (queryArg) => ({
      url: `/api/project_shifts/${queryArg.id}`,
      method: 'PATCH',
      body: queryArg.body,
    }),
  });

export const putProjectShiftsConfirmBriefing = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): MutationDefinition<
  IProjectShiftConfirmBriefingArgs,
  BaseQueryFn,
  string,
  IProjectShift
> =>
  build.mutation<IProjectShift, IProjectShiftConfirmBriefingArgs>({
    invalidatesTags: ['ProjectShift'],
    query: (queryArg) => ({
      url: `/api/project_shifts/${queryArg.id}`,
      method: 'PATCH',
      body: {
        isBriefed: true,
      },
    }),
  });

export const putProjectShiftRemind = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): MutationDefinition<
  IProjectShiftRemindPutArgs,
  BaseQueryFn,
  string,
  IProjectShift
> =>
  build.mutation<IProjectShift, IProjectShiftRemindPutArgs>({
    invalidatesTags: (_result, _error, args) =>
      args.skipInvalidateTags
        ? []
        : ['ProjectShift', 'ProjectMission', 'Project'],
    query: (queryArg) => ({
      url: `api/project_shifts/${queryArg.id}/remind`,
      method: 'PATCH',
      body: {},
    }),
  });

export const putProjectShiftLivemode = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): MutationDefinition<
  IProjectShiftLivemodePutArgs,
  BaseQueryFn,
  string,
  IProjectShift
> =>
  build.mutation<IProjectShift, IProjectShiftLivemodePutArgs>({
    invalidatesTags: (_result, _error, args) =>
      args.skipInvalidateTags
        ? []
        : ['ProjectShift', 'ProjectMission', 'Project'],
    query: (queryArg) => ({
      url: `/api/project_shifts/${queryArg.id}/livemode`,
      method: 'PATCH',
      body: queryArg.body,
    }),
  });

export const putProjectShiftItemStatus = (
  build: EndpointBuilder<BaseQueryFn, string, string>
): MutationDefinition<
  IProjectShiftPutStatusArgs,
  BaseQueryFn,
  string,
  IProjectShift
> =>
  build.mutation<IProjectShift, IProjectShiftPutStatusArgs>({
    invalidatesTags: (_result, _error, args) =>
      args.skipInvalidateTags
        ? []
        : ['ProjectShift', 'ProjectMission', 'Project'],
    query: (queryArg) => ({
      url: `/api/project_shifts/${queryArg.id}`,
      method: 'PATCH',
      body: queryArg.body,
    }),
  });
